import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Typography,
  Form,
  Input,
  Select,
  Button,
  Space,
  Row,
  Col,
  DatePicker,
  InputNumber,
  Switch,
  Divider,
  message,
  Tabs,
  Grid,
  Alert,
} from 'antd';
import { SaveOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { adminApi } from '@/services/api';
import { ConfigVersionSelect } from '@/components/ConfigVersionSelect';
import { apiErrorMessage } from '@/utils/apiError';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { useBreakpoint } = Grid;

interface LiveEventFormValues {
  name: string;
  description?: string;
  type: string;
  status?: string;
  platform: string;
  targetCountries: string;
  minBuildVersion: number;
  tileStartTime: dayjs.Dayjs;
  tileEndTime: dayjs.Dayjs;
  eventStartTime: dayjs.Dayjs;
  eventEndTime: dayjs.Dayjs;
  assets?: {
    tileImageUrl?: string;
    backgroundImageUrl?: string;
    cupImageUrl?: string;
    logoImageUrl?: string;
    prizeImageUrl?: string;
  };
  colorScheme?: {
    primary?: string;
    secondary?: string;
    background?: string;
    text?: string;
    accent?: string;
  };
  adConfig?: {
    android?: {
      interstitialAdId?: string;
      rewardedAdId?: string;
    };
    ios?: {
      interstitialAdId?: string;
      rewardedAdId?: string;
    };
  };
  gameConfig?: {
    pointsPerWin?: number;
    pointsPerLoss?: number;
    predictionMultiplier?: number;
    matchMinIntervalSeconds?: number;
    scheduleMaxSeconds?: number;
    entryFee?: number;
    gameMode?: string;
  };
  content?: {
    infoHeading?: string;
    infoMessage?: string;
    privacyPolicyUrl?: string;
    termsConditionsUrl?: string;
  };
  flags?: {
    isTesting?: boolean;
    enablePredictions?: boolean;
    enableLeaderboard?: boolean;
    enablePointsTable?: boolean;
    requireRegistration?: boolean;
  };
}

export const LiveEventForm: React.FC = () => {
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  // Opened from the Cricket Calendar: "+ Live event for this series".
  const calendarFixtureIds = (searchParams.get('fixtures') ?? '')
    .split(',')
    .map((f) => f.trim())
    .filter(Boolean);
  const calendarSeries = searchParams.get('series');
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(!!id);

  const isEdit = !!id;

  const fetchEvent = useCallback(async () => {
    try {
      const response = await adminApi.getLiveEvent(id!);
      const event = response.data;

      // Convert dates to dayjs
      form.setFieldsValue({
        ...event,
        tileStartTime: dayjs(event.tileStartTime),
        tileEndTime: dayjs(event.tileEndTime),
        eventStartTime: dayjs(event.eventStartTime),
        eventEndTime: dayjs(event.eventEndTime),
      });
    } catch (error) {
      message.error('Failed to load event');
      navigate('/live-events');
    } finally {
      setInitialLoading(false);
    }
  }, [id, form, navigate]);

  useEffect(() => {
    if (id) {
      fetchEvent();
    }
  }, [id, fetchEvent]);

  const handleSubmit = async (values: LiveEventFormValues) => {
    setLoading(true);
    try {
      const payload = {
        ...values,
        tileStartTime: values.tileStartTime?.toISOString(),
        tileEndTime: values.tileEndTime?.toISOString(),
        eventStartTime: values.eventStartTime?.toISOString(),
        eventEndTime: values.eventEndTime?.toISOString(),
      };

      let eventId = id;
      if (isEdit) {
        await adminApi.updateLiveEvent(id!, payload);
        message.success('Event updated successfully');
      } else {
        const created = await adminApi.createLiveEvent(payload);
        eventId = created.data?.id ?? created.data?.data?.id;
        message.success('Event created successfully');
      }
      if (calendarFixtureIds.length > 0) {
        // The event is saved; adding the matches is a second call that can fail on its own.
        try {
          if (!eventId) throw new Error('The new event has no id');
          // The API takes at most 100 matches per call.
          let created = 0;
          for (let i = 0; i < calendarFixtureIds.length; i += 100) {
            const added = await adminApi.addSchedulesFromFixtures(
              eventId,
              calendarFixtureIds.slice(i, i + 100)
            );
            created += added.data.created;
          }
          message.success(`${created} match${created === 1 ? '' : 'es'} added from the calendar`);
        } catch (addError) {
          message.warning(
            `${apiErrorMessage(addError, 'Could not add the calendar matches')}. Use "Add matches from calendar" on the event's Match Schedules tab.`,
            8
          );
        }
        navigate(eventId ? `/live-events/${eventId}` : '/live-events');
        return;
      }
      navigate('/live-events');
    } catch (error) {
      message.error(isEdit ? 'Failed to update event' : 'Failed to create event');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return <Card loading />;
  }

  return (
    <div>
      <div className="page-header">
        <Space wrap>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/live-events')} />
          <div>
            <Title level={screens.xs ? 4 : 3} style={{ margin: 0 }}>
              {isEdit ? 'Edit Live Event' : 'Create Live Event'}
            </Title>
            <Text type="secondary">
              {isEdit
                ? 'Update event configuration and settings'
                : 'Configure a new live tournament event'}
            </Text>
          </div>
        </Space>
      </div>

      {calendarFixtureIds.length > 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={`${calendarFixtureIds.length} match${calendarFixtureIds.length === 1 ? '' : 'es'} from the cricket calendar${
            calendarSeries ? ` (${calendarSeries})` : ''
          } will be added to the event as schedules when you ${isEdit ? 'update' : 'create'} it.`}
          description="Each match gets its start time, teams and an end time from its format. You can remove or edit any of them on the event's Match Schedules tab."
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          name: !isEdit && calendarSeries ? calendarSeries : undefined,
          platform: 'all',
          targetCountries: 'all',
          minBuildVersion: 1,
          status: 'draft',
          gameConfig: {
            pointsPerWin: 100,
            pointsPerLoss: 10,
            predictionMultiplier: 2,
            matchMinIntervalSeconds: 30,
            scheduleMaxSeconds: 300,
            gameMode: 'qp',
          },
          flags: {
            isTesting: false,
            enablePredictions: true,
            enableLeaderboard: true,
            enablePointsTable: true,
            requireRegistration: false,
          },
        }}
      >
        <Tabs
          items={[
            {
              key: 'basic',
              label: 'Basic Info',
              children: (
                <Row gutter={24}>
                  <Col xs={24} lg={16}>
                    <Card title="Event Details">
                      <Form.Item
                        name="name"
                        label="Event Name"
                        rules={[{ required: true, message: 'Please enter event name' }]}
                      >
                        <Input placeholder="e.g., IPL 2024 Live" maxLength={100} />
                      </Form.Item>

                      <Form.Item name="description" label="Description">
                        <TextArea rows={3} placeholder="Event description..." />
                      </Form.Item>

                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item name="type" label="Event Type" rules={[{ required: true }]}>
                            <Select>
                              <Select.Option value="world_cup">World Cup</Select.Option>
                              <Select.Option value="ipl">IPL</Select.Option>
                              <Select.Option value="t20_league">T20 League</Select.Option>
                              <Select.Option value="test_series">Test Series</Select.Option>
                              <Select.Option value="special_event">Special Event</Select.Option>
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item name="status" label="Status">
                            <Select disabled={!isEdit}>
                              <Select.Option value="draft">Draft</Select.Option>
                              <Select.Option value="scheduled">Scheduled</Select.Option>
                              <Select.Option value="tile_visible">Tile Visible</Select.Option>
                              <Select.Option value="active">Active</Select.Option>
                              <Select.Option value="completed">Completed</Select.Option>
                              <Select.Option value="cancelled">Cancelled</Select.Option>
                            </Select>
                          </Form.Item>
                        </Col>
                      </Row>
                    </Card>

                    <Card title="Schedule" style={{ marginTop: 16 }}>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            name="tileStartTime"
                            label="Tile Start Time"
                            rules={[{ required: true }]}
                          >
                            <DatePicker showTime style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="tileEndTime"
                            label="Tile End Time"
                            rules={[{ required: true }]}
                          >
                            <DatePicker showTime style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            name="eventStartTime"
                            label="Event Start Time"
                            rules={[{ required: true }]}
                          >
                            <DatePicker showTime style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="eventEndTime"
                            label="Event End Time"
                            rules={[{ required: true }]}
                          >
                            <DatePicker showTime style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Card>
                  </Col>

                  <Col xs={24} lg={8}>
                    <Card title="Targeting">
                      <Form.Item name="platform" label="Platform">
                        <Select>
                          <Select.Option value="all">All Platforms</Select.Option>
                          <Select.Option value="android">Android Only</Select.Option>
                          <Select.Option value="ios">iOS Only</Select.Option>
                        </Select>
                      </Form.Item>

                      <Form.Item name="targetCountries" label="Target Countries">
                        <Input placeholder="all or comma-separated codes (e.g., IN,US,UK)" />
                      </Form.Item>

                      <Form.Item
                        name="configVersionId"
                        label="Config Version"
                        extra="Leave empty to apply to every build. Pick a config version to limit this to the app versions mapped to it."
                      >
                        <ConfigVersionSelect style={{ width: '100%' }} />
                      </Form.Item>

                      <Form.Item name="minBuildVersion" label="Min Build Version">
                        <InputNumber min={1} style={{ width: '100%' }} />
                      </Form.Item>
                    </Card>

                    <Card title="Features" style={{ marginTop: 16 }}>
                      <Form.Item
                        name={['flags', 'enablePredictions']}
                        label="Enable Predictions"
                        valuePropName="checked"
                      >
                        <Switch />
                      </Form.Item>
                      <Form.Item
                        name={['flags', 'enableLeaderboard']}
                        label="Enable Leaderboard"
                        valuePropName="checked"
                      >
                        <Switch />
                      </Form.Item>
                      <Form.Item
                        name={['flags', 'enablePointsTable']}
                        label="Enable Points Table"
                        valuePropName="checked"
                      >
                        <Switch />
                      </Form.Item>
                      <Form.Item
                        name={['flags', 'requireRegistration']}
                        label="Require Registration"
                        valuePropName="checked"
                      >
                        <Switch />
                      </Form.Item>
                      <Form.Item
                        name={['flags', 'isTesting']}
                        label="Testing Mode"
                        valuePropName="checked"
                      >
                        <Switch />
                      </Form.Item>
                    </Card>
                  </Col>
                </Row>
              ),
            },
            {
              key: 'game',
              label: 'Game Config',
              children: (
                <Card title="Game Configuration">
                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item name={['gameConfig', 'pointsPerWin']} label="Points Per Win">
                        <InputNumber min={0} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name={['gameConfig', 'pointsPerLoss']} label="Points Per Loss">
                        <InputNumber min={0} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        name={['gameConfig', 'predictionMultiplier']}
                        label="Prediction Multiplier"
                      >
                        <InputNumber min={1} step={0.5} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item
                        name={['gameConfig', 'matchMinIntervalSeconds']}
                        label="Min Match Interval (sec)"
                      >
                        <InputNumber min={0} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        name={['gameConfig', 'scheduleMaxSeconds']}
                        label="Schedule Grace Period (sec)"
                      >
                        <InputNumber min={0} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name={['gameConfig', 'entryFee']} label="Entry Fee (coins)">
                        <InputNumber min={0} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item name={['gameConfig', 'gameMode']} label="Game Mode">
                    <Select style={{ width: 200 }}>
                      <Select.Option value="qp">Quick Play</Select.Option>
                      <Select.Option value="bm">Batting Multiplayer</Select.Option>
                    </Select>
                  </Form.Item>
                </Card>
              ),
            },
            {
              key: 'assets',
              label: 'Assets & Branding',
              children: (
                <Row gutter={24}>
                  <Col xs={24} lg={12}>
                    <Card title="Image URLs">
                      <Form.Item name={['assets', 'tileImageUrl']} label="Tile Image URL">
                        <Input placeholder="https://..." />
                      </Form.Item>
                      <Form.Item
                        name={['assets', 'backgroundImageUrl']}
                        label="Background Image URL"
                      >
                        <Input placeholder="https://..." />
                      </Form.Item>
                      <Form.Item name={['assets', 'cupImageUrl']} label="Cup Image URL">
                        <Input placeholder="https://..." />
                      </Form.Item>
                      <Form.Item name={['assets', 'logoImageUrl']} label="Logo Image URL">
                        <Input placeholder="https://..." />
                      </Form.Item>
                      <Form.Item name={['assets', 'prizeImageUrl']} label="Prize Image URL">
                        <Input placeholder="https://..." />
                      </Form.Item>
                    </Card>
                  </Col>
                  <Col xs={24} lg={12}>
                    <Card title="Color Scheme">
                      <Form.Item name={['colorScheme', 'primary']} label="Primary Color">
                        <Input placeholder="#1890ff" />
                      </Form.Item>
                      <Form.Item name={['colorScheme', 'secondary']} label="Secondary Color">
                        <Input placeholder="#52c41a" />
                      </Form.Item>
                      <Form.Item name={['colorScheme', 'background']} label="Background Color">
                        <Input placeholder="#ffffff" />
                      </Form.Item>
                      <Form.Item name={['colorScheme', 'text']} label="Text Color">
                        <Input placeholder="#000000" />
                      </Form.Item>
                      <Form.Item name={['colorScheme', 'accent']} label="Accent Color">
                        <Input placeholder="#ff4d4f" />
                      </Form.Item>
                    </Card>
                  </Col>
                </Row>
              ),
            },
            {
              key: 'ads',
              label: 'Ad Config',
              children: (
                <Row gutter={24}>
                  <Col xs={24} lg={12}>
                    <Card title="Android Ad IDs">
                      <Form.Item
                        name={['adConfig', 'android', 'interstitialAdId']}
                        label="Interstitial Ad ID"
                      >
                        <Input placeholder="ca-app-pub-xxx" />
                      </Form.Item>
                      <Form.Item
                        name={['adConfig', 'android', 'rewardedAdId']}
                        label="Rewarded Ad ID"
                      >
                        <Input placeholder="ca-app-pub-xxx" />
                      </Form.Item>
                    </Card>
                  </Col>
                  <Col xs={24} lg={12}>
                    <Card title="iOS Ad IDs">
                      <Form.Item
                        name={['adConfig', 'ios', 'interstitialAdId']}
                        label="Interstitial Ad ID"
                      >
                        <Input placeholder="ca-app-pub-xxx" />
                      </Form.Item>
                      <Form.Item name={['adConfig', 'ios', 'rewardedAdId']} label="Rewarded Ad ID">
                        <Input placeholder="ca-app-pub-xxx" />
                      </Form.Item>
                    </Card>
                  </Col>
                </Row>
              ),
            },
            {
              key: 'content',
              label: 'Content',
              children: (
                <Card title="Content & Messaging">
                  <Form.Item name={['content', 'infoHeading']} label="Info Heading">
                    <Input placeholder="Welcome to the tournament!" />
                  </Form.Item>
                  <Form.Item name={['content', 'infoMessage']} label="Info Message">
                    <TextArea rows={3} placeholder="Detailed information..." />
                  </Form.Item>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name={['content', 'privacyPolicyUrl']} label="Privacy Policy URL">
                        <Input placeholder="https://..." />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name={['content', 'termsConditionsUrl']}
                        label="Terms & Conditions URL"
                      >
                        <Input placeholder="https://..." />
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>
              ),
            },
          ]}
        />

        <Divider />

        <Form.Item>
          <Space>
            <Button onClick={() => navigate('/live-events')}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={loading} icon={<SaveOutlined />}>
              {isEdit ? 'Update Event' : 'Create Event'}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
};

export default LiveEventForm;
